import { App } from '@capacitor/app';
import { Preferences } from '@capacitor/preferences';
import { PushNotifications } from '@capacitor/push-notifications';
import type {
  PushNotificationSchema,
  ActionPerformed,
  Token,
} from '@capacitor/push-notifications';

import i18n from '@shared/i18n';
import { apiClient } from '@shared/lib/api/client';

import { native } from './index';

type Navigator = { navigate: (to: string) => void };

const INSTALL_ID_KEY = 'push_install_id';
const DEVICES_ENDPOINT = '/api/v1/notifications/devices/';

/** Stable per-install identifier: `<appId>:<installId>`. */
async function getDeviceId(): Promise<string> {
  let appId = 'app';
  try {
    const info = await App.getInfo();
    appId = info.id || appId;
  } catch {
    // @capacitor/app unavailable (web) — keep the default.
  }
  const stored = await Preferences.get({ key: INSTALL_ID_KEY });
  let installId = stored.value;
  if (!installId) {
    installId =
      globalThis.crypto?.randomUUID?.() ??
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    await Preferences.set({ key: INSTALL_ID_KEY, value: installId });
  }
  return `${appId}:${installId}`;
}

async function getAppVersion(): Promise<string> {
  try {
    const info = await App.getInfo();
    return info.version || '';
  } catch {
    return '';
  }
}

/** POST the freshly issued push token to the backend (idempotent upsert). */
async function registerDevice(token: string): Promise<void> {
  const [deviceId, appVersion] = await Promise.all([getDeviceId(), getAppVersion()]);
  await apiClient.post(DEVICES_ENDPOINT, {
    platform: native.platform,
    device_id: deviceId,
    push_token: token,
    app_version: appVersion,
    os_version: '',
    locale: i18n.language || 'en',
  });
}

/**
 * Wire up push notifications on the native app.
 *
 * No-op on web. Requests permission, registers for a token, forwards the token
 * to the backend, surfaces foreground notifications as in-app toasts, and routes
 * background taps to the notification's `deep_link`.
 *
 * @returns a teardown function removing all listeners.
 */
export async function initPushNotifications(navigatorApi: Navigator): Promise<() => void> {
  const noop = () => {};
  if (!native.isNative) return noop;

  const permission = await PushNotifications.requestPermissions();
  if (permission.receive !== 'granted') return noop;

  await PushNotifications.register();

  const handles = await Promise.all([
    PushNotifications.addListener('registration', (token: Token) => {
      void registerDevice(token.value).catch(() => {
        // Network error — the next app open re-registers.
      });
    }),
    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      // Foreground delivery: surface as an in-app toast via a DOM event so the
      // active React tree can render it (see the toast provider).
      window.dispatchEvent(
        new CustomEvent('push:received', {
          detail: {
            title: notification.title ?? '',
            body: notification.body ?? '',
            data: notification.data ?? {},
          },
        }),
      );
    }),
    PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      const deepLink = action.notification?.data?.deep_link;
      if (typeof deepLink === 'string' && deepLink) {
        navigatorApi.navigate(deepLink);
      }
    }),
  ]);

  return () => {
    handles.forEach((handle) => {
      void handle.remove();
    });
  };
}
